import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import VideoFeedItem from "../components/VideoFeedItem";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, Upload, Flame, Music, Bitcoin, Swords, Spade, Map as MapIcon, Megaphone } from "lucide-react";

export default function Home() {
    const { user } = useAuth();
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/videos")
            .then((r) => alive && setVideos(r.data))
            .catch(() => alive && setVideos([]))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            {/* Hero */}
            <section data-testid="home-hero" className="mb-12 lg:mb-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                    <div className="lg:col-span-8">
                        <div className="flex flex-wrap gap-2 mb-6">
                            <Link
                                to="/try-video-creator"
                                data-testid="hero-try-video-creator-badge"
                                className="inline-flex items-center gap-2 bg-vibrantpink text-white border-[3px] border-ink rounded-full px-4 py-1.5 shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg transition-all"
                            >
                                <Sparkles className="w-4 h-4" strokeWidth={2.5} />
                                <span className="font-heading font-bold text-xs uppercase tracking-widest">Try Video Creator</span>
                            </Link>
                            <Link
                                to="/try-music-creator"
                                data-testid="hero-try-music-creator-badge"
                                className="inline-flex items-center gap-2 bg-vibrantblue text-white border-[3px] border-ink rounded-full px-4 py-1.5 shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg transition-all"
                            >
                                <Music className="w-4 h-4" strokeWidth={2.5} />
                                <span className="font-heading font-bold text-xs uppercase tracking-widest">Try Music Creator</span>
                            </Link>
                            <Link
                                to="/bet-crypto"
                                data-testid="hero-bet-crypto-badge"
                                className="inline-flex items-center gap-2 bg-vibrantorange text-white border-[3px] border-ink rounded-full px-4 py-1.5 shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg transition-all"
                            >
                                <Bitcoin className="w-4 h-4" strokeWidth={2.5} />
                                <span className="font-heading font-bold text-xs uppercase tracking-widest">Bet Markets</span>
                            </Link>
                            <Link
                                to="/poker"
                                data-testid="hero-poker-badge"
                                className="inline-flex items-center gap-2 bg-vibrantpink text-white border-[3px] border-ink rounded-full px-4 py-1.5 shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg transition-all"
                            >
                                <Spade className="w-4 h-4" strokeWidth={2.5} />
                                <span className="font-heading font-bold text-xs uppercase tracking-widest">Poker</span>
                            </Link>
                            <Link
                                to="/kyc"
                                data-testid="hero-kyc-badge"
                                className="inline-flex items-center gap-2 bg-ink text-white border-[3px] border-ink rounded-full px-4 py-1.5 shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg transition-all"
                            >
                                <Megaphone className="w-4 h-4" strokeWidth={2.5} />
                                <span className="font-heading font-bold text-xs uppercase tracking-widest">KYC Promos</span>
                            </Link>
                        </div>
                        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter text-ink leading-[0.95]">
                            AI actors, musicians<br />
                            <span className="bg-vibrantorange text-white px-2 inline-block -rotate-1">the new meta</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base sm:text-lg font-medium text-ink/70 leading-relaxed">
                            Upload bold videos, set your own advertising budget and fee.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {user && user !== false ? (
                                <Link to="/upload" data-testid="hero-upload-btn" className="brutal-btn-primary">
                                    <Upload className="w-4 h-4 mr-2" strokeWidth={2.5} /> Upload your video
                                </Link>
                            ) : (
                                <>
                                    <Link to="/register" data-testid="hero-register-btn" className="brutal-btn-primary">
                                        Create your account
                                    </Link>
                                    <Link to="/login" data-testid="hero-login-btn" className="brutal-btn-secondary">
                                        Log in
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="brutal-card p-6 bg-vibrantyellow">
                            <div className="flex items-center gap-2 mb-3">
                                <Flame className="w-5 h-5 text-vibrantorange" strokeWidth={2.5} />
                                <span className="font-heading font-bold text-sm uppercase tracking-widest text-ink">How it works</span>
                            </div>
                            <ul className="space-y-2 font-body font-semibold text-ink">
                                <li className="flex items-start gap-2"><span className="text-vibrantorange">●</span> Upload a video</li>
                                <li className="flex items-start gap-2"><span className="text-vibrantblue">●</span> Set advertising budget and fee</li>
                                <li className="flex items-start gap-2"><span className="text-vibrantpink">●</span> Pay others to advertise or get paid to advertise</li>
                                <li className="flex items-start gap-2"><span className="text-ink">●</span> Enjoy viral marketing</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feed */}
            <section data-testid="home-feed-section">
                <div className="flex items-end justify-between mb-6">
                    <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-ink">
                        Latest drops
                    </h2>
                    <div className="hidden sm:block font-heading font-bold text-xs uppercase tracking-widest text-ink/60">
                        {videos.length} video{videos.length === 1 ? "" : "s"}
                    </div>
                </div>

                {loading ? (
                    <div data-testid="feed-loading" className="font-heading font-bold text-lg text-ink/60">Loading the feed...</div>
                ) : videos.length === 0 ? (
                    <div data-testid="feed-empty" className="brutal-card p-10 text-center bg-cream">
                        <h3 className="font-heading font-black text-2xl text-ink mb-2">No videos yet</h3>
                        <p className="font-body text-ink/70 mb-6">Be the first to drop something wild.</p>
                        {user && user !== false ? (
                            <Link to="/upload" className="brutal-btn-primary inline-flex">
                                <Upload className="w-4 h-4 mr-2" strokeWidth={2.5} /> Upload now
                            </Link>
                        ) : (
                            <Link to="/register" className="brutal-btn-primary inline-flex">Get started</Link>
                        )}
                    </div>
                ) : (
                    <div data-testid="feed-grid" className="space-y-8 lg:space-y-10">
                        {videos.map((v, idx) => (
                            <VideoFeedItem
                                key={v.id}
                                video={v}
                                index={idx}
                                onDeleted={(deletedId) =>
                                    setVideos((prev) => prev.filter((x) => x.id !== deletedId))
                                }
                            />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
